import { Header, Sidebar } from '@/components/layout';
import { ClinicalTemplateDialog } from '@/components/templates/ClinicalTemplateDialog';
import { ClinicalTemplateTable, IconTableButton } from '@/components/templates/ClinicalTemplateTable';
import { TemplateScopeTabs } from '@/components/templates/TemplateScopeTabs';
import { TodoAssignmentTable } from '@/components/templates/TodoAssignmentTable';
import {
  type ClinicalTemplate,
  type ClinicalTemplateFormState,
  type TodoAssignment,
  clinicalTemplatePayloadFromForm,
  emptyClinicalTemplateForm,
  fetchJson,
  formFromTemplate,
  visibilityLabelOptionsFromTemplates,
} from '@/components/templates/template-utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';

interface TemplatesOrgDetailProps {
  orgId: string;
}

export default function TemplatesOrgDetail({ orgId }: TemplatesOrgDetailProps) {
  const [editingTemplate, setEditingTemplate] = useState<ClinicalTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<ClinicalTemplateFormState>(emptyClinicalTemplateForm);
  const [isLoadingTemplateDetail, setIsLoadingTemplateDetail] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['templates', 'org', orgId],
    queryFn: () => fetchJson<ClinicalTemplate[]>(`/api/templates/orgs/${orgId}`),
  });
  const { data: assignmentPayload, isLoading: isLoadingAssignments } = useQuery({
    queryKey: ['templates', 'todo-assignments', orgId],
    queryFn: () =>
      fetchJson<{ assignments?: TodoAssignment[] }>(
        `/api/templates/todo/assignments?auth_org_id=${encodeURIComponent(orgId)}`
      ),
  });
  const assignments = assignmentPayload?.assignments || [];

  const saveTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ClinicalTemplateFormState }) => {
      const { visibility_labels: labels = [], ...payload } = clinicalTemplatePayloadFromForm(data, {
        includeVisibilityLabels: true,
      });
      await fetchJson(`/api/templates/org/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return fetchJson(`/api/templates/org/${id}/visibility`, {
        method: 'PUT',
        body: JSON.stringify({ labels }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'org', orgId] });
      setEditingTemplate(null);
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að vista stofnunarsniðmát.', variant: 'destructive' });
    },
  });

  const toggleAssignmentMutation = useMutation({
    mutationFn: (assignment: TodoAssignment) =>
      fetchJson(`/api/templates/todo/assignments/${assignment.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !assignment.is_active }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'todo-assignments', orgId] });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að breyta stöðu verkefnasniðs.', variant: 'destructive' });
    },
  });

  const openEdit = async (template: ClinicalTemplate) => {
    setIsLoadingTemplateDetail(true);
    try {
      const fullTemplate = await fetchJson<ClinicalTemplate>(`/api/templates/${template.id}`);
      setEditingTemplate(fullTemplate);
      setTemplateForm(formFromTemplate(fullTemplate));
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að sækja stofnunarsniðmát.', variant: 'destructive' });
    } finally {
      setIsLoadingTemplateDetail(false);
    }
  };

  const saveTemplate = (formValues: ClinicalTemplateFormState) => {
    if (!editingTemplate) return;
    saveTemplateMutation.mutate({ id: editingTemplate.id, data: formValues });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Stofnunarsniðmát" />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-6xl space-y-6">
            <TemplateScopeTabs active="org" />

            <div>
              <h1 className="font-bold text-2xl text-slate-900">Stofnunarsniðmát</h1>
              <p className="text-muted-foreground text-sm">Stofnun: {orgId}</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Klínísk sniðmát</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingTemplates ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <ClinicalTemplateTable
                    templates={templates}
                    emptyText="Engin stofnunarsniðmát fundust."
                    renderActions={(template) => (
                      <IconTableButton
                        label={`Breyta ${template.name}`}
                        onClick={() => openEdit(template)}
                        disabled={isLoadingTemplateDetail}
                      >
                        <Pencil className="h-4 w-4" />
                      </IconTableButton>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-lg leading-none tracking-tight">Verkefnasnið</h2>
              </CardHeader>
              <CardContent>
                {isLoadingAssignments ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <TodoAssignmentTable
                    assignments={assignments}
                    onToggle={toggleAssignmentMutation.mutate}
                    isToggling={toggleAssignmentMutation.isPending}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      <ClinicalTemplateDialog
        open={!!editingTemplate}
        onOpenChange={(open) => !open && setEditingTemplate(null)}
        title="Breyta stofnunarsniðmáti"
        description="Kerfisstjóri getur uppfært efni og merki/hlutverk sniðmátsins."
        form={templateForm}
        onSubmit={saveTemplate}
        isSaving={saveTemplateMutation.isPending}
        showVisibilityLabels
        visibilityLabelOptions={visibilityLabelOptionsFromTemplates(templates, templateForm.visibility_labels)}
      />
    </div>
  );
}
